// API client — HTTP utility with auth token injection + auto-refresh on 401

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/** Whether a token refresh is currently in-flight (prevents parallel refreshes) */
let refreshPromise: Promise<string | null> | null = null;

/** Typed fetch wrapper with auth token injection and automatic 401 retry */
export async function apiClient<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('accessToken');

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: options.credentials ?? 'include',
    headers: {
      // Only set JSON content-type when there's a body to send
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // Auto-refresh on 401 (expired access token) — retry once
  if (response.status === 401 && token && !path.includes('/auth/refresh')) {
    const newToken = await tryRefreshToken();
    if (newToken) {
      // Retry original request with new token
      const retryResponse = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: options.credentials ?? 'include',
        headers: {
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        },
      });

      if (!retryResponse.ok) {
        const error = await retryResponse
          .json()
          .catch(() => ({ error: { message: 'Request failed' } }));
        throw new ApiClientError(
          retryResponse.status,
          error.error?.code ?? 'UNKNOWN',
          error.error?.message,
        );
      }
      return retryResponse.json() as Promise<T>;
    }

    // Refresh failed — clear session and redirect to login
    localStorage.removeItem('accessToken');
    window.location.href = '/login';
    throw new ApiClientError(401, 'SESSION_EXPIRED', 'Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Network error' } }));
    throw new ApiClientError(response.status, error.error?.code ?? 'UNKNOWN', error.error?.message);
  }

  return response.json() as Promise<T>;
}

/**
 * Attempt to refresh the access token using the httpOnly refresh cookie.
 * Deduplicates parallel refresh calls — only one request in-flight at a time.
 */
async function tryRefreshToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) return null;

      const data = await res.json();
      const newToken = data?.data?.accessToken;
      if (newToken) {
        localStorage.setItem('accessToken', newToken);
        return newToken as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** API client error with status code and error code */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
